import {
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  Text,
  VStack,
  Tab,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  TabIndicator,
  Box,
  Spinner,
  Center,
} from '@vimeo/bokeh';
import { DownloadFilesContent } from '@vimeo/download-files';
import type { TextTrack } from '@vimeo/gctl-api/models/components/schemas/text-track/TextTrack';
import type { VideoFile } from '@vimeo/gctl-api/models/components/schemas/video-file/VideoFile';
import { translate } from '@vimeo/i18n-runtime';

import DownloadTranscriptsContent from './components/DownloadTranscriptsContent';
import { ModuleHeader } from '../../components/ModuleHeader';

export type DownloadListVideoFile = Pick<
  VideoFile,
  | 'height'
  | 'link'
  | 'publicName'
  | 'quality'
  | 'size'
  | 'sizeShort'
  | 'type'
  | 'videoFileId'
  | 'width'
>;
export type DownloadListTextTrack = Pick<
  TextTrack,
  'active' | 'displayLanguage' | 'id' | 'language' | 'link' | 'type'
>;

interface DownloadListContentProps {
  /** Video download files source */
  videoFiles: DownloadListVideoFile[];
  /** Text track download files source */
  texttracks: DownloadListTextTrack[];
  /** video id */
  videoId?: string;
  /** for cases when clip_id:clip_hash needs to be used to fetch video data */
  clipRequestId?: string;
  onTranscriptDownload?: (language: string) => void;
  isLoadingVideoFiles?: boolean;
}

export interface Props extends DownloadListContentProps {
  /** handler fired when clicking close button on download list */
  onClose?: VoidFunction;
  /** Whether modal is open */
  isOpen?: boolean;
  /** Whether to display component as modal */
  showAsModal?: boolean;
}

export const DownloadList = ({
  isOpen = false,
  onClose,
  showAsModal = false,
  videoFiles,
  texttracks,
  videoId,
  clipRequestId,
  onTranscriptDownload,
  isLoadingVideoFiles,
}: Props) => {
  let header = translate('Download');
  if (!isLoadingVideoFiles && videoFiles.length === 0 && texttracks.length >= 1) {
    header = translate('Download transcript');
  } else if (!isLoadingVideoFiles && videoFiles.length >= 1 && texttracks.length === 0) {
    header = translate('Download video');
  }

  if (!showAsModal)
    return (
      <VStack w="inherit" h="100%" overflowY="auto" align="stretch">
        <ModuleHeader text={header} onClose={onClose} />
        <Box px="300">
          <DownloadListContent
            videoFiles={videoFiles}
            texttracks={texttracks}
            videoId={videoId}
            clipRequestId={clipRequestId}
            onTranscriptDownload={onTranscriptDownload}
            isLoadingVideoFiles={isLoadingVideoFiles}
          />
        </Box>
      </VStack>
    );

  return (
    <Modal isOpen={isOpen} onClose={() => onClose?.()} size="sm">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text variant="heading-md" as="h4">
            {header}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py="300" borderBottomRadius="xl">
          <DownloadListContent
            videoFiles={videoFiles}
            texttracks={texttracks}
            videoId={videoId}
            clipRequestId={clipRequestId}
            onTranscriptDownload={onTranscriptDownload}
            isLoadingVideoFiles={isLoadingVideoFiles}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const DownloadListContent = ({
  videoFiles,
  videoId,
  clipRequestId,
  texttracks,
  onTranscriptDownload,
  isLoadingVideoFiles,
}: DownloadListContentProps) => {
  const registerClick = (lang: string | null) => {
    lang && onTranscriptDownload?.(lang);
  };

  // Render video list without tabs if there are no transcripts to download
  if (texttracks.length === 0) {
    return (
      <Flex direction="column" w="100%" py="100">
        {isLoadingVideoFiles ? (
          <Loader />
        ) : (
          <DownloadFilesContent
            files={videoFiles as VideoFile[]}
            videoID={videoId ? parseInt(videoId) : undefined}
            clipRequestId={clipRequestId}
            pageName="viewer_home"
          />
        )}
      </Flex>
    );
  }

  // Render transcript list without tabs if there are no videos to download
  if (videoFiles.length === 0) {
    return (
      <DownloadTranscriptsContent files={texttracks as TextTrack[]} registerClick={registerClick} />
    );
  }

  // Render in tabs if both videos and transcripts are available to download
  return (
    <Tabs variant="inlay" size="sm" width="100%" defaultIndex={0}>
      <TabList>
        <Tab>{translate('Video')}</Tab>
        <Tab>{translate('Transcript')}</Tab>
        <TabIndicator />
      </TabList>
      <TabPanels>
        <TabPanel>
          <Flex direction="column" w="100%" py="100">
            {isLoadingVideoFiles ? (
              <Loader />
            ) : (
              <DownloadFilesContent
                files={videoFiles as VideoFile[]}
                videoID={videoId ? parseInt(videoId) : undefined}
                clipRequestId={clipRequestId}
                pageName="viewer_home"
              />
            )}
          </Flex>
        </TabPanel>
        <TabPanel>
          <DownloadTranscriptsContent
            files={texttracks as TextTrack[]}
            registerClick={registerClick}
          />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

const Loader = () => (
  <Center py="xl">
    <Spinner />
  </Center>
);
